<template>
  <BasePage :pageTitle="__DICT__.actionMenu.eventAccreditation" back-button show-menu show-footer>
    <div class="card mb-4">
      <div class="card-body">
        <div class="card-status-top bg-primary"></div>
        <div class="list-group list-group-flush">
          <template v-for="event in sortedEvents" :key="event.eventId">
            <router-link
                v-if="canViewAll || event?.brands.split(',').includes(__DICT__.brand.toUpperCase())"
                :to="`/ticket/${event.eventId}`"
                class="list-group-item list-group-item-action">
              {{ event.eventName }} <br/>

              <span v-for="brand in event.brands.split(',')" :class="{
            'bg-red-lt': brand === 'NUCO',
            'bg-blue-lt': brand === 'WASTELAND',
          }" class="badge ms-auto mt-2">{{ brand }}</span>
              <span v-if="event.startDateTime" class="text-muted small ms-4">
                {{ formatDateTime(event.startDateTime) }}
              </span>
            </router-link>
          </template>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="text-center mt-3">
      <div class="spinner-border text-primary" role="status"></div>
      <span class="ms-2">{{ __DICT__.loading }}</span>
    </div>
  </BasePage>
</template>

<script setup>
import BasePage from "@/components/BasePage.vue";
import {api} from "@/services/api.js";
import {useWeekStore} from '@/stores/week.js'
import {ref, computed, onMounted} from "vue";

const weekStore = useWeekStore()
const canViewAll = computed(() => weekStore.repObj?.level >= 3)
const events = ref([]);
const isLoading = ref(true);
const weekData = ref([]);

// Get event dates from week data similar to EventPage.vue
const eventDates = computed(() => {
  const eventDateMap = new Map()

  weekData.value.forEach(passenger => {
    const passengerEvents = passenger.allocPlaces?.filter(place => place.event) || []

    passengerEvents.forEach(place => {
      const event = place.event
      if (!event || !event.reference) return

      const referenceId = event.reference.split('-')[2]
      if(!referenceId) return

      if (!eventDateMap.has(referenceId)) {
        eventDateMap.set(referenceId, {
          referenceId,
          startDateTime: event.startDateTime
        })
      }
    })
  })

  return eventDateMap
})

const sortedEvents = computed(() => {
  // Map events with their dates
  const eventsWithDates = events.value.map(event => {
    const eventInfo = eventDates.value.get(event.eventRef)
    return {
      ...event,
      startDateTime: eventInfo?.startDateTime || null
    }
  })

  // Sort by date if available, otherwise by name
  return eventsWithDates.sort((a, b) => {
    if (a.startDateTime && b.startDateTime) {
      return new Date(a.startDateTime) - new Date(b.startDateTime)
    }
    return a.eventName.localeCompare(b.eventName)
  })
})

function formatDateTime(dateTimeString) {
  if (!dateTimeString) return ''

  // Check if the date is already in the format "HH:MM DD/MM/YYYY"
  if (dateTimeString.match(/^\d{2}:\d{2} \d{2}\/\d{2}\/\d{4}$/)) {
    // Already in the desired format, just return it
    return dateTimeString
  }

  // Otherwise, parse and format the date
  const date = new Date(dateTimeString)
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const fetchEvents = async () => {
  try {
    const a = (await api.get(`/api/events/${weekStore.weekId}`)).data;
    events.value = a.sort((a, b) => a.id - b.id);
  } catch (error) {
    console.error('Failed to fetch events:', error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  isLoading.value = true;
  try {
    // Load week data for event dates (similar to EventPage)
    weekData.value = await weekStore.weekData;
    await fetchEvents();
  } catch (error) {
    console.error('Error loading data:', error);
    isLoading.value = false;
  }
});
</script>
